import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  TASK_DOMAIN_LABELS, PRIORITY_LABELS, priorityBadgeClass, taskTypeBadgeClass, TASK_TYPE_LABELS, isTaskOverdue,
} from "../../lib/tasks";
import { KANBAN_COLUMNS, KANBAN_QUICK_ADD, taskColumnId } from "../../lib/kanban";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "../ui/responsive-dropdown-menu";
import { Building2, ChevronsLeft, ChevronsRight, Eye, MoveRight, MoreHorizontal, Pencil, Plus, Trash2, UserRoundCog } from "lucide-react";
import AssigneeAvatars from "../AssigneeAvatars";
import { formatMobileDue } from "../TaskTimeline";

const COLLAPSED_KEY = "jamora.tasks.kanban.collapsed";

function readCollapsed() {
  try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY)) || {}; } catch { return {}; }
}

// One card. The title opens the detail drawer; the ⋯ menu is a sibling control, so neither triggers the other. Dragging needs
// `tasks:edit` (the server re-checks each move); the menu's "Déplacer vers" is the way to move a card on a phone or without a
// pointer, next to the keyboard drag (Espace, flèches) that @hello-pangea/dnd provides.
function KanbanCard({
  task, index, canEdit, canDelete, directory, resolveAssignee, resolveProspectName, onView, onEdit, onDelete, onAddAssignee, onMove,
  revertedId, navigate,
}) {
  const column = taskColumnId(task);
  const overdue = isTaskOverdue(task);
  const due = formatMobileDue(task.due_date, task.done);
  const prospectName = task.prospect_id ? resolveProspectName(task.prospect_id) : null;
  const closed = column === "termine" || column === "annulee";
  const showMenu = canEdit || canDelete;
  const reverted = revertedId === task.id;

  return (
    <Draggable draggableId={task.id} index={index} isDragDisabled={!canEdit}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
          data-testid={`kanban-card-${task.id}`}
          className={`rounded-lg border bg-white p-3 text-left select-none ${
            snapshot.isDragging ? "border-jamora shadow-lg rotate-1" : "border-slate-200 hover:border-slate-300"
          } ${overdue ? "border-l-4 border-l-red-400" : ""} ${reverted ? "card-reverted" : ""} ${canEdit ? "cursor-grab" : ""}`}
        >
          <div className="flex items-start gap-2">
            {/* Not a <button>: @hello-pangea/dnd refuses to start a drag from a native interactive element, and the title covers
                most of the card. Same behaviour for mouse, touch and keyboard (Entrée opens the detail). */}
            <div
              role="button" tabIndex={0} onClick={() => onView(task)} data-testid={`kanban-view-${task.id}`}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onView(task); } }}
              className={`flex-1 min-w-0 text-sm font-semibold text-left break-words ${closed ? "text-slate-400" : "text-slate-900"} ${column === "termine" ? "line-through" : ""}`}
            >
              {task.title}
            </div>
            {showMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button" aria-label={`Actions sur ${task.title}`} data-testid={`kanban-actions-${task.id}`}
                    className="h-7 w-7 -mr-1 -mt-1 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500 shrink-0"
                  >
                    <MoreHorizontal size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onView(task)}><Eye size={14} className="mr-2" /> Voir le détail</DropdownMenuItem>
                  {canEdit && (
                    <>
                      <DropdownMenuItem onClick={() => onEdit(task)}><Pencil size={14} className="mr-2" /> Modifier</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {KANBAN_COLUMNS.filter((c) => c.id !== column).map((c) => (
                        <DropdownMenuItem key={c.id} onClick={() => onMove(task, c.id)} data-testid={`kanban-move-${task.id}-${c.id}`}>
                          <MoveRight size={14} className="mr-2" /> Déplacer vers « {c.label} »
                        </DropdownMenuItem>
                      ))}
                      {directory.filter((u) => !(task.assigned_to || []).includes(u.id)).length > 0 && <DropdownMenuSeparator />}
                      {directory.filter((u) => !(task.assigned_to || []).includes(u.id)).map((u) => (
                        <DropdownMenuItem key={u.id} onClick={() => onAddAssignee(task, u.id)}>
                          <UserRoundCog size={14} className="mr-2" /> Ajouter {u.name} comme responsable
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" onClick={() => onDelete(task)}><Trash2 size={14} className="mr-2" /> Supprimer</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {task.priority && (
              <span className={`text-2xs font-medium px-1.5 py-0.5 rounded ${priorityBadgeClass(task.priority)}`}>{PRIORITY_LABELS[task.priority]}</span>
            )}
            <span className="text-2xs font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              {task.domain ? TASK_DOMAIN_LABELS[task.domain] : "Sans domaine"}
            </span>
            {task.task_type && task.task_type !== "autre" && (
              <span className={`text-2xs font-medium px-1.5 py-0.5 rounded ${taskTypeBadgeClass(task.task_type)}`}>{TASK_TYPE_LABELS[task.task_type]}</span>
            )}
          </div>

          {prospectName && (
            <button
              type="button" onClick={() => navigate(`/prospects/${task.prospect_id}`)}
              className="mt-2 inline-flex items-center gap-1 max-w-full text-xs text-slate-500 hover:text-jamora"
            >
              <Building2 size={12} className="shrink-0" /> <span className="truncate">{prospectName}</span>
            </button>
          )}

          <div className="mt-2 flex items-center justify-between gap-2">
            <span className={`text-xs ${overdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
              {due ? due.text : "Sans échéance"}
            </span>
            <AssigneeAvatars userIds={task.assigned_to} resolveUser={resolveAssignee} />
          </div>
        </div>
      )}
    </Draggable>
  );
}

function KanbanColumn({ column, tasks, collapsed, onToggle, canCreate, onQuickAdd, cardProps }) {
  return (
    <Droppable droppableId={column.id}>
      {(provided, snapshot) => (
        <section
          aria-label={`${column.label} (${tasks.length})`} data-testid={`kanban-column-${column.id}`}
          className={`shrink-0 snap-start rounded-xl border bg-slate-50/70 transition ${
            collapsed ? "w-14" : "w-[85vw] sm:w-[19rem] md:w-auto md:flex-1 md:min-w-[15rem]"
          } ${snapshot.isDraggingOver ? "border-jamora bg-jamora/5 ring-2 ring-jamora/30" : "border-slate-200"}`}
        >
          {collapsed ? (
            <div className="flex flex-col items-center gap-3 py-3">
              <button
                type="button" onClick={onToggle} aria-label={`Déplier la colonne ${column.label}`} data-testid={`kanban-expand-${column.id}`}
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
              >
                <ChevronsRight size={16} />
              </button>
              <span className="text-xs font-mono text-slate-500">{tasks.length}</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600 [writing-mode:vertical-rl]">{column.label}</span>
              {/* Kept mounted so a card can still be dropped on a folded column. */}
              <div ref={provided.innerRef} {...provided.droppableProps} className="h-8 w-full">{provided.placeholder}</div>
            </div>
          ) : (
            <>
              <header className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full ${column.dot}`} />
                  <h3 className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded ${column.head}`}>{column.label}</h3>
                  <span className="text-xs font-mono text-slate-500" data-testid={`kanban-count-${column.id}`}>{tasks.length}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  {canCreate && KANBAN_QUICK_ADD.includes(column.id) && (
                    <button
                      type="button" onClick={() => onQuickAdd(column.id)} aria-label={`Ajouter une tâche dans « ${column.label} »`}
                      data-testid={`kanban-add-${column.id}`}
                      className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
                    >
                      <Plus size={16} />
                    </button>
                  )}
                  <button
                    type="button" onClick={onToggle} aria-label={`Replier la colonne ${column.label}`} data-testid={`kanban-collapse-${column.id}`}
                    className="hidden md:flex h-7 w-7 items-center justify-center rounded hover:bg-slate-200 text-slate-500"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                </div>
              </header>
              <div
                ref={provided.innerRef} {...provided.droppableProps}
                className="px-2 pb-2 space-y-2 min-h-[5rem] md:h-[calc(100vh-32rem)] lg:h-[calc(100vh-24rem)] md:overflow-y-auto"
              >
                {tasks.map((task, index) => (
                  <KanbanCard key={task.id} task={task} index={index} {...cardProps} />
                ))}
                {provided.placeholder}
                {tasks.length === 0 && !snapshot.isDraggingOver && (
                  <p className="text-xs text-slate-400 text-center py-6">Aucune tâche</p>
                )}
              </div>
            </>
          )}
        </section>
      )}
    </Droppable>
  );
}

// Kanban board: one column per stored status, shared filters and permissions with the Liste/Timeline views (the page hands over
// the same filtered tasks, grouped by `lib/kanban.js`). `onMove(task, columnId)` is the page's single move handler, used by the
// drop and by every card's "Déplacer vers" menu entry so both behave the same (optimistic move, dialog for close/cancel).
export function KanbanView({
  groups, canEdit, canDelete, canCreate, directory, resolveAssignee, resolveProspectName, navigate,
  onView, onEdit, onDelete, onAddAssignee, onMove, onQuickAdd, revertedId,
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed)); } catch { /* storage unavailable: folding just isn't remembered */ }
  }, [collapsed]);

  const taskById = (id) => Object.values(groups).flat().find((t) => t.id === id);
  const onDragEnd = (result) => {
    const target = result.destination?.droppableId;
    const task = taskById(result.draggableId);
    if (!task || !target || target === taskColumnId(task)) return;
    onMove(task, target);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory md:snap-none p-2 md:p-3 scrollbar-thin" data-testid="kanban-board">
        {KANBAN_COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id} column={column} tasks={groups[column.id]} collapsed={!!collapsed[column.id]}
            onToggle={() => setCollapsed((prev) => ({ ...prev, [column.id]: !prev[column.id] }))}
            canCreate={canCreate} onQuickAdd={onQuickAdd}
            cardProps={{
              canEdit, canDelete, directory, resolveAssignee, resolveProspectName, onView, onEdit, onDelete, onAddAssignee, onMove, navigate,
              revertedId,
            }}
          />
        ))}
      </div>
    </DragDropContext>
  );
}
